import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { SkullLoader, SkullPageLoader } from '../components/SkullLoader';
import {
  Users,
  DollarSign,
  Crown,
  Calendar,
  Search,
  ChevronLeft,
  ChevronRight,
  Eye,
  Ban,
  Check,
  LogOut,
  LayoutDashboard,
  Trash2,
  Download,
  RefreshCw,
  TrendingUp,
  Loader2,
  Star,
  Shield
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  { value: '1', label: 'Gennaio' },
  { value: '2', label: 'Febbraio' },
  { value: '3', label: 'Marzo' },
  { value: '4', label: 'Aprile' },
  { value: '5', label: 'Maggio' },
  { value: '6', label: 'Giugno' },
  { value: '7', label: 'Luglio' },
  { value: '8', label: 'Agosto' },
  { value: '9', label: 'Settembre' },
  { value: '10', label: 'Ottobre' },
  { value: '11', label: 'Novembre' },
  { value: '12', label: 'Dicembre' }
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => ({
  value: String(currentYear - i),
  label: String(currentYear - i)
}));

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [adminToken, setAdminToken] = useState(localStorage.getItem('admin_token'));
  const [isLoggedIn, setIsLoggedIn] = useState(!!adminToken);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);

  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(String(new Date().getMonth() + 1));
  const [selectedYear, setSelectedYear] = useState(String(currentYear));
  const [exporting, setExporting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (isLoggedIn) {
      loadAdminData();
    }
  }, [isLoggedIn, page, searchQuery, selectedMonth, selectedYear]);

  const loadUsers = async () => {
    try {
      const usersRes = await fetch(
        `${BACKEND_URL}/api/admin/users?page=${page}&limit=20&search=${encodeURIComponent(searchQuery)}`,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData.users);
        setTotalPages(usersData.total_pages);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      });

      if (res.ok) {
        const data = await res.json();
        localStorage.setItem('admin_token', data.token);
        setAdminToken(data.token);
        setIsLoggedIn(true);
        toast.success('Login admin effettuato');
      } else {
        toast.error('Credenziali non valide');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setAdminToken(null);
    setIsLoggedIn(false);
  };

  const loadAdminData = async () => {
    setLoading(true);
    try {
      // Load stats with month filter
      const statsRes = await fetch(
        `${BACKEND_URL}/api/admin/stats?month=${selectedMonth}&year=${selectedYear}`, 
        { headers: { 'Authorization': `Bearer ${adminToken}` } }
      );
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      // Load users
      const usersRes = await fetch(
        `${BACKEND_URL}/api/admin/users?page=${page}&limit=20&search=${encodeURIComponent(searchQuery)}`,
        { headers: { 'Authorization': `Bearer ${adminToken}` } }
      );
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData.users || []);
        setTotalPages(usersData.pages || 1);
      } else if (usersRes.status === 401 || usersRes.status === 403) {
        handleLogout();
        toast.error('Sessione scaduta');
      }
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserDetails = async (userId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${userId}`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedUser(data);
        setUserDialogOpen(true);
      }
    } catch (error) {
      toast.error('Errore nel caricamento dettagli');
    }
  };

  const toggleUserActive = async (userId, isActive) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({ is_active: !isActive })
      });
      if (res.ok) {
        toast.success(isActive ? 'Utente disattivato' : 'Utente attivato');
        loadAdminData();
        setUserDialogOpen(false);
      }
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const deleteUserPermanently = async (userId) => {
    if (!window.confirm('ATTENZIONE: Questa azione è irreversibile! Tutti i dati dell\'utente verranno eliminati permanentemente. Continuare?')) {
      return;
    }
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${userId}?permanent=true`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        toast.success('Utente eliminato permanentemente');
        loadAdminData();
        setUserDialogOpen(false);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore nell\'eliminazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const toggleSuperUser = async (userId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${userId}/superuser`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        loadAdminData();
        if (selectedUser) {
          // Refresh selected user data
          const userRes = await fetch(`${BACKEND_URL}/api/admin/users/${userId}`, {
            headers: { 'Authorization': `Bearer ${adminToken}` }
          });
          if (userRes.ok) {
            setSelectedUser(await userRes.json());
          }
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/export-excel`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `vault_ride_export_${new Date().toISOString().slice(0,10)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Export completato!');
      } else {
        toast.error('Errore durante l\'export');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setExporting(false);
    }
  };

  const handleResetRevenue = async () => {
    setResetting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/reset-revenue`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Guadagni azzerati! ${data.deleted_payments} pagamenti eliminati.`);
        setResetDialogOpen(false);
        loadAdminData();
      } else {
        toast.error('Errore durante l\'azzeramento');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setResetting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-[#121212] border-[#262626]">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-[#FF5F1F]/20 flex items-center justify-center">
                <LayoutDashboard className="w-8 h-8 text-[#FF5F1F]" />
              </div>
            </div>
            <CardTitle className="text-white font-['Teko'] text-3xl uppercase">
              Admin Dashboard
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label className="text-white">Email</Label>
                <Input
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  className="bg-[#0a0a0a] border-[#262626] text-white"
                  placeholder="admin@vaultride.app"
                />
              </div>
              <div>
                <Label className="text-white">Password</Label>
                <Input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  className="bg-[#0a0a0a] border-[#262626] text-white"
                  placeholder="••••••••"
                />
              </div>
              <Button
                type="submit"
                disabled={loginLoading}
                className="w-full bg-[#FF5F1F] hover:bg-[#FF5F1F]/90"
              >
                {loginLoading ? (
                  <SkullLoader size="sm" className="mr-2" />
                ) : null}
                Accedi
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#0a0a0a] p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-['Teko'] text-white uppercase">Admin Dashboard</h1>
            </div>
            <p className="text-[#666]">Gestione utenti e abbonamenti</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={async () => {
                setRefreshing(true);
                await loadAdminData();
                setRefreshing(false);
                toast.success('Dati aggiornati');
              }}
              disabled={refreshing}
              className="border-[#262626] text-[#9CA3AF] hover:text-white hover:border-[#FF5F1F]"
            >
              {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Aggiorna
            </Button>
            <Button
              variant="outline"
              onClick={handleExportExcel}
              disabled={exporting}
              className="border-[#262626] text-[#9CA3AF] hover:text-white hover:border-[#FF5F1F]"
            >
              {exporting ? <SkullLoader size="sm" className="mr-2" /> : <Download className="w-4 h-4 mr-2" />}
              Esporta Excel
            </Button>
            <Button
              variant="outline"
              onClick={() => setResetDialogOpen(true)}
              className="border-red-500/50 text-red-500 hover:bg-red-500/10"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Azzera Guadagni
            </Button>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="border-[#262626] text-[#9CA3AF]"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Month/Year Filter */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <span className="text-[#666]">Filtra per:</span>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-[140px] bg-[#121212] border-[#262626] text-white">
                <SelectValue placeholder="Mese" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#262626]">
                {MONTHS.map(m => (
                  <SelectItem key={m.value} value={m.value} className="text-white hover:bg-[#262626]">
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-[100px] bg-[#121212] border-[#262626] text-white">
                <SelectValue placeholder="Anno" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#262626]">
                {YEARS.map(y => (
                  <SelectItem key={y.value} value={y.value} className="text-white hover:bg-[#262626]">
                    {y.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <Card className="bg-[#121212] border-[#262626]">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Users className="w-8 h-8 text-[#FF5F1F]" />
                  <div>
                    <p className="text-[#666] text-sm">Utenti Totali</p>
                    <p className="text-2xl font-bold text-white font-mono">{stats.total_users}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#121212] border-[#262626]">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Crown className="w-8 h-8 text-[#FF5F1F]" />
                  <div>
                    <p className="text-[#666] text-sm">Abbonamenti Attivi</p>
                    <p className="text-2xl font-bold text-white font-mono">{stats.active_subscriptions}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#121212] border-[#262626]">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Calendar className="w-8 h-8 text-blue-500" />
                  <div>
                    <p className="text-[#666] text-sm">Mensili / Annuali</p>
                    <p className="text-2xl font-bold text-white font-mono">
                      {stats.monthly_subscriptions} / {stats.annual_subscriptions}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#121212] border-[#262626]">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <DollarSign className="w-8 h-8 text-green-500" />
                  <div>
                    <p className="text-[#666] text-sm">Ricavi Totali</p>
                    <p className="text-2xl font-bold text-white font-mono">{stats.total_revenue} CHF</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#121212] border-[#FF5F1F]">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <TrendingUp className="w-8 h-8 text-[#FF5F1F]" />
                  <div>
                    <p className="text-[#666] text-sm">Guadagno Mensile</p>
                    <p className="text-2xl font-bold text-[#FF5F1F] font-mono">{stats.monthly_revenue} CHF</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#121212] border-[#262626]">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Users className="w-8 h-8 text-purple-500" />
                  <div>
                    <p className="text-[#666] text-sm">Nuovi Abbonati</p>
                    <p className="text-2xl font-bold text-white font-mono">{stats.new_subscribers_month}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Search */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#666]" />
            <Input
              placeholder="Cerca per nome o email..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              className="pl-10 bg-[#121212] border-[#262626] text-white"
            />
          </div>
          <Button
            onClick={loadAdminData}
            variant="outline"
            className="border-[#262626] text-[#9CA3AF]"
          >
            Aggiorna
          </Button>
        </div>

        {/* Users Table */}
        <Card className="bg-[#121212] border-[#262626]">
          <CardHeader>
            <CardTitle className="text-white">Utenti Registrati</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-[#FF5F1F]" />
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#262626]">
                        <th className="text-left py-3 px-4 text-[#666] font-normal">Nome</th>
                        <th className="text-left py-3 px-4 text-[#666] font-normal">Email</th>
                        <th className="text-left py-3 px-4 text-[#666] font-normal">Piano</th>
                        <th className="text-left py-3 px-4 text-[#666] font-normal">Stato</th>
                        <th className="text-left py-3 px-4 text-[#666] font-normal">Registrato</th>
                        <th className="text-left py-3 px-4 text-[#666] font-normal">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user.id} className="border-b border-[#262626] hover:bg-[#1a1a1a]">
                          <td className="py-3 px-4 text-white">{user.nome}</td>
                          <td className="py-3 px-4 text-[#9CA3AF] font-mono text-sm">{user.email}</td>
                          <td className="py-3 px-4">
                            {user.subscription_type ? (
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                user.subscription_type === 'annual' 
                                  ? 'bg-[#FF5F1F]/20 text-[#FF5F1F]' 
                                  : 'bg-blue-500/20 text-blue-400'
                              }`}>
                                {user.subscription_type === 'monthly' ? 'Mensile' : 'Annuale'}
                              </span>
                            ) : (
                              <span className="text-[#666] text-sm">-</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {user.is_superuser ? (
                              <span className="flex items-center gap-1 text-yellow-500 text-sm">
                                <Star className="w-3 h-3 fill-yellow-500" /> SuperUser
                              </span>
                            ) : user.is_active !== false ? (
                              <span className="flex items-center gap-1 text-green-500 text-sm">
                                <Check className="w-3 h-3" /> Attivo
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-red-500 text-sm">
                                <Ban className="w-3 h-3" /> Bloccato
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-[#666] font-mono text-sm">
                            {formatDate(user.created_at)}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => loadUserDetails(user.id)}
                                className="border-[#262626] text-[#9CA3AF] h-8"
                                title="Visualizza dettagli"
                              >
                                <Eye className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => toggleSuperUser(user.id)}
                                className={`h-8 ${
                                  user.is_superuser 
                                    ? 'border-yellow-500 bg-yellow-500/20 text-yellow-500' 
                                    : 'border-[#262626] text-[#666]'
                                }`}
                                title={user.is_superuser ? 'Rimuovi SuperUser' : 'Rendi SuperUser'}
                              >
                                <Star className={`w-3 h-3 ${user.is_superuser ? 'fill-yellow-500' : ''}`} />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => toggleUserActive(user.id, user.is_active !== false)}
                                className={`h-8 ${
                                  user.is_active !== false 
                                    ? 'border-yellow-500/50 text-yellow-500' 
                                    : 'border-green-500/50 text-green-500'
                                }`}
                                title={user.is_active !== false ? 'Blocca' : 'Riattiva'}
                              >
                                {user.is_active !== false ? <Ban className="w-3 h-3" /> : <Check className="w-3 h-3" />}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => deleteUserPermanently(user.id)}
                                className="h-8 border-red-500/50 text-red-500 hover:bg-red-500/10"
                                title="Elimina permanentemente"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#262626]">
                  <p className="text-[#666] text-sm">
                    Pagina {page} di {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={page <= 1}
                      onClick={() => setPage(page - 1)}
                      className="border-[#262626] text-[#9CA3AF]"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={page >= totalPages}
                      onClick={() => setPage(page + 1)}
                      className="border-[#262626] text-[#9CA3AF]"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* User Details Dialog */}
        <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
          <DialogContent className="bg-[#0a0a0a] border-[#262626] max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white font-['Teko'] text-2xl uppercase">
                Dettagli Utente
              </DialogTitle>
            </DialogHeader>
            {selectedUser && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[#666] text-sm">Nome</p>
                    <p className="text-white">{selectedUser.user?.nome}</p>
                  </div>
                  <div>
                    <p className="text-[#666] text-sm">Email</p>
                    <p className="text-white font-mono text-sm">{selectedUser.user?.email}</p>
                  </div>
                  <div>
                    <p className="text-[#666] text-sm">Paese</p>
                    <p className="text-white">{selectedUser.user?.paese}</p>
                  </div>
                  <div>
                    <p className="text-[#666] text-sm">Registrato il</p>
                    <p className="text-white font-mono">{formatDate(selectedUser.user?.created_at)}</p>
                  </div>
                </div>

                {selectedUser.subscription && (
                  <div className="bg-[#121212] p-4 rounded border border-[#262626]">
                    <h4 className="text-[#FF5F1F] font-semibold mb-2">Abbonamento</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <p className="text-[#666]">Piano:</p>
                      <p className="text-white">{selectedUser.subscription.plan_type}</p>
                      <p className="text-[#666]">Stato:</p>
                      <p className="text-green-500">{selectedUser.subscription.status}</p>
                      <p className="text-[#666]">Scadenza:</p>
                      <p className="text-white font-mono">{formatDate(selectedUser.subscription.end_date)}</p>
                    </div>
                  </div>
                )}

                {selectedUser.payments?.length > 0 && (
                  <div>
                    <h4 className="text-[#666] text-sm mb-2">Ultimi Pagamenti</h4>
                    {selectedUser.payments.slice(0, 3).map((p, i) => (
                      <div key={i} className="flex justify-between text-sm py-1 border-b border-[#262626]">
                        <span className="text-[#9CA3AF]">{formatDate(p.created_at)}</span>
                        <span className="text-white font-mono">{p.amount} {p.currency}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => setUserDialogOpen(false)}
                className="border-[#262626] text-[#9CA3AF]"
              >
                Chiudi
              </Button>
              {selectedUser?.user && (
                <>
                  <Button
                    onClick={() => toggleSuperUser(selectedUser.user.id)}
                    className={selectedUser.user.is_superuser 
                      ? 'bg-yellow-500 hover:bg-yellow-600' 
                      : 'bg-[#262626] hover:bg-[#333] border border-yellow-500/50'
                    }
                  >
                    <Star className={`w-4 h-4 mr-2 ${selectedUser.user.is_superuser ? 'fill-white' : 'text-yellow-500'}`} />
                    {selectedUser.user.is_superuser ? 'Rimuovi SuperUser' : 'Rendi SuperUser'}
                  </Button>
                  <Button
                    onClick={() => toggleUserActive(selectedUser.user.id, selectedUser.user.is_active !== false)}
                    className={selectedUser.user.is_active !== false ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-green-600 hover:bg-green-700'}
                  >
                    {selectedUser.user.is_active !== false ? (
                      <><Ban className="w-4 h-4 mr-2" /> Blocca</>
                    ) : (
                      <><Check className="w-4 h-4 mr-2" /> Riattiva</>
                    )}
                  </Button>
                  <Button
                    onClick={() => deleteUserPermanently(selectedUser.user.id)}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Elimina
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset Revenue Confirmation Dialog */}
        <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
          <DialogContent className="bg-[#0a0a0a] border-[#262626] max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white font-['Teko'] text-2xl uppercase">
                Conferma Azzeramento
              </DialogTitle>
              <DialogDescription className="text-[#666]">
                Sei sicuro di voler azzerare tutti i guadagni? Questa azione eliminerà permanentemente tutti i pagamenti registrati e non può essere annullata.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => setResetDialogOpen(false)}
                className="border-[#262626] text-[#9CA3AF]"
              >
                Annulla
              </Button>
              <Button
                onClick={handleResetRevenue}
                disabled={resetting}
                className="bg-red-600 hover:bg-red-700"
              >
                {resetting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Conferma Azzeramento
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
